import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { DateFormatPipe } from 'src/pipes/date-format.pipe';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomPaginatorIntlService } from 'src/services/custom-paginator-intl.service';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import { DiffusionService } from 'src/services/diffusion.service';
import { DiffusionPilotageModel } from 'src/models/pilotage/diffusionPilotageModel';
import { ScormService } from 'src/services/scorm.service';
import { ScormPackageModel } from 'src/models/scormPackageModel';
import { MatDialog } from '@angular/material/dialog';
import { AjouterDiffusionConfirmationDialogComponent } from './ajouter-diffusion-dialog/ajouter-diffusion-confirmation-dialog/ajouter-diffusion-confirmation-dialog.component';
import { DeleteDiffusionDialogComponent } from './delete-diffusion-dialog/delete-diffusion-dialog.component';
import { DiffusionPaquetsDialogComponent } from './diffusion-paquets-dialog/diffusion-paquets-dialog.component';
import { AjouterModifierDiffusionDialogComponent } from './ajouter-modifier-diffusion-dialog/ajouter-modifier-diffusion-dialog.component';
import { AjouterModifierDiffusionMultipleDialogComponent } from './ajouter-modifier-diffusion-multiple-dialog/ajouter-modifier-diffusion-multiple-dialog.component';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { EEntite } from 'src/enum/eEntite';

@Component({
    selector: 'app-diffusion-managment',
    templateUrl: './diffusion-managment.component.html',
    styleUrls: ['./diffusion-managment.component.scss'],
    imports: [
        MatProgressSpinnerModule,
        TranslateModule,
        CommonModule,
        FormsModule,
        MatIconModule,
        DateFormatPipe,
        MatTooltipModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatTableModule,
        MatPaginator,
        MatSortModule,
        MatButtonModule,
        MatMenuModule,
        MatPseudoCheckboxModule,
        ClipboardModule
    ],
    providers: [
        {
            provide: MatPaginatorIntl,
            useClass: CustomPaginatorIntlService
        }
    ]
})
export class DiffusionManagmentComponent implements OnInit {
  diffusions: DiffusionPilotageModel[] = [];
  diffusionsAffichees: DiffusionPilotageModel[] = [];
  diffusionsFiltreEncours: DiffusionPilotageModel[] = [];
  scormPackages: ScormPackageModel[] = [];

  displayedColumns: string[] = [
    'urlPlayer',
    'packageNom',
    'langue',
    'dureeVideo',
    'dateCreation',
    'actions'
  ];

  pageIndex: number = 0;
  pageSize: number = 10;
  currentPage: number = 0;
  totalItems: number = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  searchTerm: string = '';
  currentLang: string = 'fr';

  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  isLoading: boolean = false;
  estUtilisateurPilote: boolean = false;

  dataSource!: MatTableDataSource<DiffusionPilotageModel>;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private router: Router,
    public utilisateurService: UtilisateurService,
    private diffusionService: DiffusionService,
    private scormService: ScormService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private clipboard: Clipboard
  ) {
    translate.setDefaultLang('fr');
    this.dataSource = new MatTableDataSource<DiffusionPilotageModel>([]);
  }


  ngOnInit(): void {
    if (this.utilisateurService.entiteUtilisateurSelectionnee.entiteId === EEntite.ROOT_ENTITE_ID) {
      this.router.navigate(['/utilisateurs-root-managment']);
      return;
    }

    this.currentLang = this.translate.currentLang || 'fr';

    this.translate.onLangChange.subscribe((event) => {
      this.currentLang = event.lang;
      this.changeDetectorRef.detectChanges();
    });

    this.estUtilisateurPilote = this.estPilote();

    this.loadDiffusions();
    this.loadScormPackages();
  }



  applyFilters(): void {
    this.pageIndex = 0;

    this.diffusionsFiltreEncours = this.diffusions.filter(corr => this.applyDiffusionFilter(corr));

    this.totalItems = this.diffusionsFiltreEncours.length;
    this.paginer();
  }

  sortDataChange(sortState: Sort) {
    const data = this.diffusionsFiltreEncours.slice();

    if (!sortState.active || sortState.direction === '') {
      this.sortColumn = '';
      this.sortDirection = '';
      this.diffusionsFiltreEncours = [...this.diffusions.filter(corr => {
        return this.applyDiffusionFilter(corr);
      })];
      this.paginer();
      return;
    }

    this.sortColumn = sortState.active;
    this.sortDirection = sortState.direction;

    this.diffusionsFiltreEncours = data.sort((a, b) => {
      const isAsc = sortState.direction === 'asc';

      switch (sortState.active) {
        case 'urlPlayer':
          return this.compareStrings(a.urlPlayer, b.urlPlayer, isAsc);
        case 'packageNom':
          const packageNomA = a.scormPackageNomLocales[0]?.[this.currentLang] || a.scormPackageNomLocales[0]?.['fr'] || a.scormPackageNom;
          const packageNomB = b.scormPackageNomLocales[0]?.[this.currentLang] || b.scormPackageNomLocales[0]?.['fr'] || b.scormPackageNom;
          return this.compareStrings(packageNomA, packageNomB, isAsc);
        case 'langue':
          return this.compareStrings(a.langue, b.langue, isAsc);
        case 'dureeVideo':
          const dureeA = a.dureeVideo || 0;
          const dureeB = b.dureeVideo || 0;
          return this.compareNumbers(dureeA, dureeB, isAsc);
        case 'dateCreation':
          const dateA = new Date(a.dateCreation).getTime();
          const dateB = new Date(b.dateCreation).getTime();
          return this.compareNumbers(dateA, dateB, isAsc);
        default:
          return 0;
      }
    });

    this.paginer();
  }

  private compareStrings(a: string, b: string, isAsc: boolean): number {
    const valueA = (a || '').toLowerCase().trim();
    const valueB = (b || '').toLowerCase().trim();

    const result = valueA.localeCompare(valueB, 'fr', {
      numeric: true,
      sensitivity: 'base',
      ignorePunctuation: true
    });

    return isAsc ? result : -result;
  }

  private compareNumbers(a: number, b: number, isAsc: boolean): number {
    const result = a - b;
    return isAsc ? result : -result;
  }

  private applyDiffusionFilter(diffusion: DiffusionPilotageModel): boolean {
    if (!this.searchTerm) {
      return true;
    }

    const searchLower = this.searchTerm.toLowerCase().trim();

    const displayedFormationName = (
      diffusion.scormPackageNomLocales?.[0]?.[this.currentLang] ||
      diffusion.scormPackageNomLocales?.[0]?.['fr'] ||
      diffusion.scormPackageNom ||
      ''
    ).toLowerCase();

    const searchableFields = [
      displayedFormationName,
      diffusion.urlPlayer?.toLowerCase(),
      diffusion.langue?.toLowerCase()
    ];

    return searchableFields.some(field =>
      field && field.includes(searchLower)
    );
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.sortColumn = '';
    this.sortDirection = '';
    this.pageIndex = 0;
    this.applyFilters();

    if (this.sort) {
      this.sort.active = '';
      this.sort.direction = '';
      this.sort._stateChanges.next();
    }
  }


  ngAfterViewInit() {
    if (this.sort) {
      this.dataSource.sort = this.sort;
      this.sort.sortChange.subscribe(() => {
        this.paginator.pageIndex = 0;
      });
    }

    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
  }


  estPilote(): boolean {
    return this.utilisateurService.selectionEntiteUtilisateur.estRoot
      || this.utilisateurService.entiteUtilisateurSelectionnee.roleEntites
        .some(roleEntite => roleEntite.roleId === 1);
  }

  private loadDiffusions(): Promise<void> {
    return new Promise((resolve) => {
      this.isLoading = true;
      this.diffusionService.obtenirDiffusionsParEntite(
        this.utilisateurService.entiteUtilisateurSelectionnee.entiteId,
        this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId
      ).subscribe(
        res => {
          this.diffusions = res;
          this.diffusionsFiltreEncours = [...this.diffusions];
          this.applyFilters();
          this.isLoading = false;
          resolve();
        },
        error => {
          console.error('Error loading diffusions:', error);
          this.isLoading = false;
          resolve();
        }
      );
    });
  }

  private loadScormPackages(): void {
    this.scormService.getAllPackages(
      this.utilisateurService.entiteUtilisateurSelectionnee.entiteId
    ).subscribe(
      res => {
        this.scormPackages = res;
      },
      error => {
        console.error('Error loading scorm packages:', error);
      }
    );
  }

  get estRootLectureSeule(): boolean {
    return this.utilisateurService.selectionEntiteUtilisateur?.estRootLectureSeule || false;
  }

  paginer(event?: PageEvent) {
    if (event) {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
    }

    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.diffusionsAffichees = this.diffusionsFiltreEncours.slice(startIndex, endIndex);
    this.dataSource.data = this.diffusionsAffichees;
    this.changeDetectorRef.detectChanges();
  }

  private updatePaginatorLength(): void {
    if (this.paginator) {
      this.paginator.length = this.diffusionsFiltreEncours.length;
      this.totalItems = this.diffusionsFiltreEncours.length;
      this.paginator.page.emit();
      this.changeDetectorRef.detectChanges();
    }
  }

  refreshDataSource(): void {
    this.diffusionsFiltreEncours = this.diffusions;
    this.dataSource.data = this.diffusionsFiltreEncours;
    this.updatePaginatorLength();
  }

  ouvrirDialogAjout(): void {
    const dialogRef = this.dialog.open(AjouterModifierDiffusionDialogComponent, {
      width: '600px',
      data: { mode: 'ajouter', scormPackages: this.scormPackages }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.scormPackageId && result.langue) {
        this.isLoading = true;
        this.diffusionService.ajouterDiffusion(
          result.scormPackageId,
          result.langue,
          this.utilisateurService.entiteUtilisateurSelectionnee.entiteId,
          this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId
        ).subscribe({
          next: (diffusionCreee) => {
            this.isLoading = false;
            this.dialog.open(AjouterDiffusionConfirmationDialogComponent, {
              width: '600px',
              data: {
                success: true
              }
            });
            this.loadDiffusions();
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Erreur lors de la création de la diffusion:', error);
            this.dialog.open(AjouterDiffusionConfirmationDialogComponent, {
              width: '600px',
              data: {
                success: false,
                errorMessage: error.error?.message || error.message || 'Une erreur est survenue'
              }
            });
          }
        });
      }
    });
  }

  ouvrirDialogAjoutMultiple(): void {
    const dialogRef = this.dialog.open(AjouterModifierDiffusionMultipleDialogComponent, {
      width: '600px',
      data: { mode: 'ajouter', scormPackages: this.scormPackages }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.scormPackageIds && result.scormPackageIds.length > 0 && result.langue) {
        this.isLoading = true;
        this.diffusionService.ajouterDiffusionMultiple(
          result.scormPackageIds,
          result.titre,
          result.langue,
          this.utilisateurService.entiteUtilisateurSelectionnee.entiteId,
          this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId
        ).subscribe({
          next: (diffusionCreee) => {
            this.isLoading = false;
            this.dialog.open(AjouterDiffusionConfirmationDialogComponent, {
              width: '600px',
              data: { success: true }
            });
            this.loadDiffusions();
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Erreur lors de la création de la diffusion multiple:', error);
            this.dialog.open(AjouterDiffusionConfirmationDialogComponent, {
              width: '600px',
              data: {
                success: false,
                errorMessage: error.error?.message || error.message || 'Une erreur est survenue'
              }
            });
          }
        });
      }
    });
  }

  onModifierDiffusion(diffusion: DiffusionPilotageModel): void {
    const estMultiple = (diffusion.scormPackageIds?.length ?? 0) > 1;

    if (estMultiple) {
      const dialogRef = this.dialog.open(AjouterModifierDiffusionMultipleDialogComponent, {
        width: '600px',
        data: { mode: 'modifier', diffusion, scormPackages: this.scormPackages }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result && result.scormPackageIds && result.scormPackageIds.length > 0 && result.langue) {
          this.isLoading = true;
          this.diffusionService.modifierDiffusionMultiple(
            diffusion.diffusionId,
            result.scormPackageIds,
            result.titre,
            result.langue,
            this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId
          ).subscribe({
            next: () => {
              this.isLoading = false;
              this.loadDiffusions();
            },
            error: (error) => {
              this.isLoading = false;
              console.error('Erreur lors de la modification de la diffusion multiple:', error);
            }
          });
        }
      });
    } else {
      const dialogRef = this.dialog.open(AjouterModifierDiffusionDialogComponent, {
        width: '600px',
        data: { mode: 'modifier', diffusion, scormPackages: this.scormPackages }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result && result.scormPackageId && result.langue) {
          this.isLoading = true;
          this.diffusionService.modifierDiffusion(
            diffusion.diffusionId,
            result.scormPackageId,
            result.titre,
            result.langue,
            this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId
          ).subscribe({
            next: () => {
              this.isLoading = false;
              this.loadDiffusions();
            },
            error: (error) => {
              this.isLoading = false;
              console.error('Erreur lors de la modification de la diffusion:', error);
            }
          });
        }
      });
    }
  }

  supprimerDiffusion(diffusion: DiffusionPilotageModel): void {
    const dialogRef = this.dialog.open(DeleteDiffusionDialogComponent, {
      width: '600px',
      data: {
        diffusionId: diffusion.diffusionId,
        utilisateurId: this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadDiffusions();
      }
    });
  }

  copierUrl(url: string): void {
    const success = this.clipboard.copy(url);

    if (success) {
      this.snackBar.open(
        this.translate.instant('DIFFUSION.URL_COPIED'),
        this.translate.instant('COMMUN.CLOSE'),
        { duration: 3000 }
      );
    } else {
      console.error('Erreur lors de la copie');
      this.snackBar.open(
        this.translate.instant('DIFFUSION.COPY_ERROR'),
        this.translate.instant('COMMUN.CLOSE'),
        { duration: 3000 }
      );
    }
  }

  getPackageNom(element: DiffusionPilotageModel): string {
    return element.scormPackageNomLocales?.[0]?.[this.currentLang]
      || element.scormPackageNomLocales?.[0]?.['fr']
      || element.scormPackageNom;
  }

  ouvrirPopupPaquets(element: DiffusionPilotageModel): void {
    this.dialog.open(DiffusionPaquetsDialogComponent, {
      width: '500px',
      data: {
        titre: element.titre,
        scormPackageNomLocales: element.scormPackageNomLocales
      }
    });
  }

  getLangueLabel(langue: string): string {
    const langKey = `DIFFUSION.LANGUAGE_${langue.toUpperCase()}`;
    return this.translate.instant(langKey);
  }

  formatDureeVideo(dureeSec: number | null | undefined): string {
    if (!dureeSec || dureeSec <= 0) {
      return '-';
    }

    const heures = Math.floor(dureeSec / 3600);
    const minutes = Math.floor((dureeSec % 3600) / 60);
    const secondes = dureeSec % 60;

    if (heures > 0) {
      return `${heures}:${String(minutes).padStart(2, '0')}:${String(secondes).padStart(2, '0')}`;
    }

    return `${minutes}:${String(secondes).padStart(2, '0')}`;
  }
}